import React, { useEffect } from 'react';
import {
    Users, Clock, Mail, ShieldCheck, AlertTriangle,
    LogIn, CheckCircle, Info, ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';

const Step = ({ number, icon: Icon, iconClass, title, tag, tagClass, children }) => (
    <div className="flex gap-4 sm:gap-6">
        <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconClass}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 w-px bg-accent-main/20 mt-2" />
        </div>
        <div className="pb-8 flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-sm font-bold text-text-muted uppercase tracking-widest">Step {number}</span>
                {tag && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tagClass}`}>{tag}</span>
                )}
            </div>
            <h3 className="text-lg font-bold text-primary-dark mb-2">{title}</h3>
            <div className="text-primary-dark/80 space-y-1">{children}</div>
        </div>
    </div>
);

export default function NomineeAccessInfoPage() {
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';

    useEffect(() => {
        document.title = 'Nominee Access | Eternal Locker™';
    }, []);

    return (
        <>
            <Header type="small" showAccountDropdown={isLoggedIn} />
            <div className="flex justify-center w-full font-sans">
                <div className="w-full max-w-7xl p-8 space-y-6 bg-background-light shadow-2xl rounded-xl border border-accent-main">
                    {/* Page header */}
                    <div className="text-center space-y-2 mb-6">
                        <h1 className="text-3xl font-bold text-accent-main">How Nominee Access Works</h1>

                    </div>

                    <div className="flex flex-col gap-8 p-4 bg-primary-light border border-accent-main rounded-xl shadow">
                        <p className="text-lg text-justify">
                            Eternal Locker™ automatically grants your designated nominees access to your locker
                            if your account remains inactive and you do not respond to our verification emails.
                            Here is how the process works.
                        </p>
                        {/* What triggers the process */}
                        <div>
                            <h2 className="text-2xl font-semibold text-accent-main flex items-center gap-2">
                                <Clock className="w-5 h-5 flex-shrink-0" /> What Triggers the Process
                            </h2>
                            <p className="text-justify mt-3">
                                The system monitors the date of your last successful login. If your account shows
                                <strong className="text-accent-main"> a prolonged period of inactivity</strong>,
                                an automated verification process begins. It is designed to give you multiple
                                chances to confirm you are still active before any access is granted to nominees.
                            </p>
                        </div>

                        {/* Timeline */}
                        <div>
                            <h2 className="text-2xl font-semibold text-accent-main mb-4 flex items-center gap-2">
                                <ChevronRight className="w-5 h-5 flex-shrink-0" /> Step-by-Step Timeline
                            </h2>

                            <Step
                                number={1}
                                icon={Mail}
                                iconClass="bg-blue-100 text-blue-700"
                                title="First Verification Email"
                            >
                                <p>
                                    Once the inactivity threshold is reached, the system identifies the account and sends
                                    a verification email to the registered email address with a unique
                                    <em> Confirm I'm Active</em> link.
                                </p>
                                <p className="mt-1">
                                    Your account is temporarily marked as inactive at this point. No nominee access is granted yet.
                                </p>
                            </Step>

                            <Step
                                number={2}
                                icon={Mail}
                                iconClass="bg-yellow-100 text-yellow-700"
                                title="Second Reminder Email"
                            >
                                <p>
                                    If the first email is ignored and you have not confirmed your activity, a <strong>second
                                        reminder</strong> is sent a few days later with a new confirmation link.
                                </p>
                            </Step>

                            <Step
                                number={3}
                                icon={AlertTriangle}
                                iconClass="bg-orange-100 text-orange-700"
                                title="Final Warning Email"
                            >
                                <p>
                                    A <strong>final warning email</strong> is sent shortly after the second reminder.
                                    This is your last opportunity to confirm activity and prevent nominee access from being
                                    granted. The email clearly states that access will be granted soon if no action
                                    is taken.
                                </p>
                            </Step>

                            <Step
                                number={4}
                                icon={ShieldCheck}
                                iconClass="bg-red-100 text-red-700"
                                title="Nominee Access Granted"
                            >
                                <p>
                                    If all three emails go unanswered and you have not logged in, nominee access is
                                    <strong className="text-red-600"> automatically granted</strong>.
                                </p>
                                <ul className="mt-2 list-disc list-inside space-y-1">
                                    <li>You receive a final notification email confirming the grant.</li>
                                    <li>Each of your registered nominees receives an email with login instructions.</li>
                                </ul>
                            </Step>

                            <Step
                                number={5}
                                icon={Users}
                                iconClass="bg-green-100 text-green-700"
                                title="Nominee Access Workflow"
                                tag="Important"
                                tagClass="bg-red-100 text-red-700"
                            >
                                <p>
                                    Once nominee access is granted, nominees can only <em>view</em> your stored information.
                                    They cannot modify or delete any data. Nominee logins and account access events are logged for security and transparency.
                                </p>
                            </Step>
                        </div>

                        {/* How to prevent it */}
                        <div>
                            <h2 className="text-2xl font-semibold text-accent-main flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 flex-shrink-0" /> How to Stay in Control
                            </h2>
                            <div className="grid sm:grid-cols-2 gap-4 text-primary-dark/90 mt-3">
                                <div className="flex items-start gap-3 p-4 bg-background-light border border-accent-main/30 rounded-lg">
                                    <LogIn className="w-5 h-5 text-accent-main flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-primary-dark mb-1">Log in regularly</p>
                                        <p>Logging in at any time completely resets the inactivity clock. No emails will be sent to an active account.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-4 bg-background-light border border-accent-main/30 rounded-lg">
                                    <Mail className="w-5 h-5 text-accent-main flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-primary-dark mb-1">Respond to any reminder</p>
                                        <p>Clicking the <em>Confirm I'm Active</em> link immediately stops the verification process and reactivates your account.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Important notes */}
                        <div>
                            <h2 className="text-2xl font-semibold text-accent-main flex items-center gap-2">
                                <Info className="w-5 h-5 flex-shrink-0" /> Important Notes
                            </h2>
                            <ul className="list-disc list-inside space-y-2 text-justify mt-3">
                                <li>
                                    Nominee access is only granted for accounts that have at least one entry in the
                                    <strong> Nominee Information</strong> category. If no nominees are registered, the process
                                    stops before access is granted.
                                </li>
                                <li>
                                    All verification emails are sent to the email address you used to sign up.
                                    Make sure it remains accessible.
                                </li>
                                <li>
                                    Nominee logins and account access events are recorded for security and transparency.
                                </li>
                            </ul>
                        </div>

                        {/* Disclaimer */}
                        <p className="text-justify text-red-500">
                            <strong>Disclaimer:</strong> The automated nominee access feature is provided as a convenience
                            tool to help your loved ones access critical information when genuinely needed. Eternal Locker™
                            and Nybble Technologies Private Limited are not responsible for any consequences arising from
                            nominee access granted through this automated process. Users are responsible for keeping their
                            nominee list accurate and their registered email address accessible at all times.
                        </p>
                    </div>

                </div>
            </div>
        </>
    );
}
